// Append-only encoding for div card rewards (decode side — used by poe2filter).
// 1. Booleans: add a named bit at the next free bit; update encode (scraper) + decode.
// 2. Other fields: append a new pipe slot at the end; update FIELD indices only.
// 3. Old encoded strings stay valid (missing trailing fields = absent).

export type DivCardRewardRarity =
  | 'unique'
  | 'rare'
  | 'magic'
  | 'normal'
  | 'gem'
  | 'currency'
  | 'divination';

export interface DivCardReward {
  stackSize: number;
  name: string;
  rarity?: DivCardRewardRarity;
  quantity?: number;
  gemLevel?: number;
  itemLevel?: number;
  mapTier?: number;
  quality?: number;
  implicits?: number;
  implicitRange?: [number, number];
  influences?: string[];
  corrupted?: boolean;
  foulborn?: boolean;
  unidentified?: boolean;
  fractured?: boolean;
  synthesised?: boolean;
  sixLink?: boolean;
  influenced?: boolean;
  doubleInfluenced?: boolean;
  disabled?: boolean;
}

export const DivCardRewardFlag = {
  Corrupted: 1 << 0,
  Foulborn: 1 << 1,
  Unidentified: 1 << 2,
  Fractured: 1 << 3,
  Synthesised: 1 << 4,
  SixLink: 1 << 5,
  Influenced: 1 << 6,
  DoubleInfluenced: 1 << 7,
  Disabled: 1 << 8,
} as const;

const CODE_TO_RARITY: Record<string, DivCardRewardRarity> = {
  u: 'unique',
  r: 'rare',
  m: 'magic',
  w: 'normal',
  g: 'gem',
  c: 'currency',
  d: 'divination',
};

// Pipe field indices (append-only):
// 0 stackSize | 1 flags | 2 name | 3 rarity | 4 quantity | 5 gemLevel | 6 itemLevel
// 7 mapTier | 8 quality | 9 implicits | 10 implicitRange | 11 influences

export function decodeDivCardReward(encoded: string): DivCardReward {
  const parts = encoded.split('|');
  const stackSize = parseInt(parts[0] || '0', 10) || 0;
  const flags = parseInt(parts[1] || '0', 10) || 0;

  const reward: DivCardReward = {
    stackSize,
    name: parts[2] ?? '',
  };

  if (parts[3]) {
    const rarity = CODE_TO_RARITY[parts[3]];
    if (rarity) reward.rarity = rarity;
  }

  const quantity = parseOptionalInt(parts[4]);
  if (quantity != null) reward.quantity = quantity;

  const gemLevel = parseOptionalInt(parts[5]);
  if (gemLevel != null) reward.gemLevel = gemLevel;

  const itemLevel = parseOptionalInt(parts[6]);
  if (itemLevel != null) reward.itemLevel = itemLevel;

  const mapTier = parseOptionalInt(parts[7]);
  if (mapTier != null) reward.mapTier = mapTier;

  const quality = parseOptionalInt(parts[8]);
  if (quality != null) reward.quality = quality;

  const implicits = parseOptionalInt(parts[9]);
  if (implicits != null) reward.implicits = implicits;

  if (parts[10]) {
    const rangeMatch = parts[10].match(/^(\d+)-(\d+)$/);
    if (rangeMatch) {
      reward.implicitRange = [parseInt(rangeMatch[1], 10), parseInt(rangeMatch[2], 10)];
    }
  }

  if (parts[11]) {
    reward.influences = parts[11].split(',').filter(Boolean);
  }

  if (flags & DivCardRewardFlag.Corrupted) reward.corrupted = true;
  if (flags & DivCardRewardFlag.Foulborn) reward.foulborn = true;
  if (flags & DivCardRewardFlag.Unidentified) reward.unidentified = true;
  if (flags & DivCardRewardFlag.Fractured) reward.fractured = true;
  if (flags & DivCardRewardFlag.Synthesised) reward.synthesised = true;
  if (flags & DivCardRewardFlag.SixLink) reward.sixLink = true;
  if (flags & DivCardRewardFlag.Influenced) reward.influenced = true;
  if (flags & DivCardRewardFlag.DoubleInfluenced) reward.doubleInfluenced = true;
  if (flags & DivCardRewardFlag.Disabled) reward.disabled = true;

  return reward;
}

// Labels for tooltip display of notable reward conditions.
export function formatDivCardRewardFlags(reward: DivCardReward): string | undefined {
  const parts: string[] = [];
  if (reward.quantity != null && reward.quantity > 1) parts.push(`x${reward.quantity}`);
  if (reward.corrupted) parts.push('Corrupted');
  if (reward.foulborn) parts.push('Foulborn');
  if (reward.unidentified) parts.push('Unidentified');
  if (reward.fractured) parts.push('Fractured');
  if (reward.synthesised) parts.push('Synthesised');
  if (reward.sixLink) parts.push('6-Link');
  if (reward.influences?.length) {
    parts.push(...reward.influences);
  } else if (reward.doubleInfluenced) {
    parts.push('Double Influenced');
  } else if (reward.influenced) {
    parts.push('Influenced');
  }
  if (reward.itemLevel != null) parts.push(`ilvl ${reward.itemLevel}`);
  return parts.length ? parts.join(', ') : undefined;
}

function parseOptionalInt(value: string | undefined): number | undefined {
  if (value == null || value === '') return undefined;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : undefined;
}
